import React, { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import Layout from 'shared/layouts/Main'
import { Button, Form } from 'react-bootstrap'
import { useForm } from 'react-hook-form'
import { useParams } from 'react-router-dom'
import InputWrapper from 'shared/components/InputWrapper'
import SelectWrapper from 'shared/components/SelectWrapper'
import resAddFilter from './resolvers/resAddFilter'
import useInit from './hooks/useInit'
import useFocusError from 'shared/hooks/useFocusError'
import prepareSubmitForm from 'shared/utils/prepareSubmitForm'
import submitForm from './utils/submitForm'
import getBrandContent from 'utils/getBrandContent'
import Footer from 'shared/components/Footer'
import BackButton from 'shared/components/BackButton'
import ManageFilterValues from 'components/ManageFilterValues'
import ModalEditableProps from 'components/EditModal/types/ModalEditableProps'
import prepareSubmitFormFromModal from 'components/EditModal/utils/prepareSubmitFormFromModal'
import useDisplaySpinner from 'components/ManageFilterValues/hooks/useDisplaySpinner'
import { syncTypes } from 'consts'

const submitRef = React.createRef<HTMLButtonElement>()

const AddFilter: React.FunctionComponent<ModalEditableProps> = React.forwardRef(
  (props: ModalEditableProps, ref) => {
    const [showOverlay, setShowOverlay] = useState<boolean>(true)
    const [dataLoaded, setDataLoaded] = useState<boolean>(false)
    const [parentFilters, setParentFilters] = useState<any>([])
    const [connections, setConnections] = useState<any>([])

    const [currentCustomer, setCurrentCustomer] = useState<{} | null>({})

    
    const { id } = useParams()
    
    const [filterLoaded, setFilterLoaded] = useState<boolean>(false)
    const [filterValuesLoaded, setFilterValuesLoaded] = useState<boolean>(id === undefined)


    const navigate = useNavigate()

    const [filterId] = useState<string | undefined>(props.modalConfig?.entityId ?? id)

    const [colAttributes] = useState<any>(props.modalConfig !== undefined ? { md: 12 } : undefined)

    const {
      control,
      register,
      getValues,
      handleSubmit,
      reset,
      setFocus,
      setError,
      watch,
      setValue,
      trigger,
      formState: { errors },
    } = useForm({
      mode: 'all',
      resolver: resAddFilter,
    })

    useInit({
      getValues,
      id: filterId,
      reset,
      setDataLoaded,
      setParentFilters,
      setConnections,
      setFilterLoaded,
      currentCustomer,
    })

    useDisplaySpinner({
      setShowOverlay,
      filterValuesLoaded,
      filterLoaded
    })

    useFocusError({
      errors,
      setFocus,
    })

    const unCheckFilterDefault = () => {
      setValue('select_all_is_default', false)
    }

    const isMulti : boolean = watch('is_multi')
    
    const syncEnabled : boolean = watch('sync')
    const connectionValue = watch('connection')
    const syncType = watch('syncType')
    const tableNameValue = watch('table_name')

    const showSyncFieldsDisabled : boolean = useMemo(() => connectionValue !== null || syncType !== null || tableNameValue?.trim()?.length > 0, [connectionValue, syncType, tableNameValue])


    useEffect(() => {
        if(!syncEnabled){
          trigger(['syncType', 'connection', 'table_name'])
        }
    }, [syncEnabled, trigger])

    const renderView = (innerContent: any) => {
      return props.modalConfig === undefined ? (
        <Layout
          footerContent={<Footer />}
          brandContent={getBrandContent()}
          showOverlay={showOverlay}
          showCardHeader={true}
          headerInsert={<BackButton url='/filters' text='All Filters' />}
          actionInsert={
            <Button
              disabled={!dataLoaded}
              type='submit'
              name='save'
              className='btn-round btn-primary'
              onClick={() => {
                if (submitRef.current != null) {
                  submitRef.current.click()
                }
              }}
            >
              Save Filter
            </Button>
          }
        >
          {innerContent}
        </Layout>
      ) : (
        innerContent
      )
    }

    return renderView(
      <>
        <Form
          onSubmit={handleSubmit((data) => {
            const commonParams = {
              submitForm,
              data,
              setShowOverlay,
              setError,
              id: filterId,
              name: 'Filter',
              successNav: '/filters',
            }

            return props.modalConfig !== undefined
              ? prepareSubmitFormFromModal({
                  ...commonParams,
                  modalConfig: props.modalConfig,
                })
              : prepareSubmitForm({
                  ...commonParams,
                  navigate: navigate,
                })
          })}
          id='main-form'
        >
          <InputWrapper colAttributes={colAttributes} label='Name' name='name' errors={errors}>
            <Form.Control type='text' {...register('name')} />
          </InputWrapper>

          <InputWrapper
            colAttributes={colAttributes}
            label='Parameter name'
            name='param'
            errors={errors}
          >
            <Form.Control type='text' {...register('param')} />
          </InputWrapper>

          <InputWrapper
            colAttributes={colAttributes}
            label='Parent Filter'
            name='parentFilter'
            errors={errors}
          >
            <SelectWrapper
              name='parentFilter'
              isClearable={true}
              control={control}
              options={parentFilters}
              optionLabel='name'
              optionValue='id'
            />
          </InputWrapper>

          <InputWrapper colAttributes={colAttributes} label='Multi Value' name='is_multi' errors={errors}>
            <Form.Check {...register('is_multi')}  />
          </InputWrapper>

          {isMulti === true && (
           <InputWrapper colAttributes={colAttributes} label='Display Select All Button' name='display_select_all' errors={errors}>
            <Form.Check {...register('display_select_all')}  />
           </InputWrapper>
          )}
             
          {isMulti === true && (
            <InputWrapper
              colAttributes={colAttributes}
              label='Select All Options Label'
              name='select_all_label'
              errors={errors}
            >
              <Form.Control type='text' {...register('select_all_label')} />
            </InputWrapper>
          )}
          
          {isMulti === true && (
            <InputWrapper
              colAttributes={colAttributes}
              label='Select All Options Value'
              name='select_all_value'
              errors={errors}
            >
              <Form.Control type='text' {...register('select_all_value')} />
            </InputWrapper>
          )}

          {isMulti === true && (
           <InputWrapper colAttributes={colAttributes} label='Make Select All Options Default' name='select_all_is_default' errors={errors}>
            <Form.Check {...register('select_all_is_default')}  />
           </InputWrapper>
          )}


          <InputWrapper colAttributes={colAttributes} label='Enable Sync' name='sync' errors={errors}>
            <Form.Check {...register('sync')}  />
          </InputWrapper>

           {(syncEnabled === true || showSyncFieldsDisabled) && (
            <InputWrapper
            colAttributes={colAttributes}
            label='Sync Type'
            name='syncType'
            errors={errors}
          >
            <SelectWrapper
              name='syncType'
              isClearable={true}
              isDisabled={!syncEnabled}
              control={control}
              options={syncTypes}
              optionLabel='name'
              optionValue='id'
            />
          </InputWrapper>
          )}

          {(syncEnabled === true || showSyncFieldsDisabled) && (
            <InputWrapper
            colAttributes={colAttributes}
            label='Connection'
            name='connection'
            errors={errors}
          >
            <SelectWrapper
              name='connection'
              isClearable={true}
              isDisabled={!syncEnabled}
              control={control}
              options={connections}
              optionLabel='name'
              optionValue='id'
            />
          </InputWrapper>
          )}


           {(syncEnabled === true || showSyncFieldsDisabled )&& (
            <InputWrapper
              colAttributes={colAttributes}
              label='Table Name'
              name='table_name'
              errors={errors}
            >
              <Form.Control readOnly={!syncEnabled} type='text' {...register('table_name')}  className={!syncEnabled ? 'read-only' : undefined} />
            </InputWrapper>
          )}


          <InputWrapper colAttributes={colAttributes} label='Active' name='active' errors={errors}>
            <Form.Select {...register('active')}>
              <option key={0} value={1}>
                Yes
              </option>
              <option key={1} value={0}>
                No
              </option>
            </Form.Select>
          </InputWrapper>

          <Button
            type='submit'
            name='save'
            ref={(ref as any) ?? submitRef}
            className='d-none'
          ></Button>
        </Form>

        {props.modalConfig === undefined && id !== undefined && (
          <ManageFilterValues filterId={id} isMulti={getValues('is_multi')} unCheckFilterDefault={unCheckFilterDefault} setShowOverlay={setShowOverlay}  setFilterValuesLoaded={setFilterValuesLoaded} />
        )}
      </>,
    )
  },
)

export default AddFilter
